import React, { HTMLProps, ReactElement, useState } from "react";
import styled from "styled-components";
import { ReactNode as TypedReactNode } from "../../../../sharedTypes";
import { ToggleTipProps } from "../../layout/toggle_tip";

interface Props extends HTMLProps<HTMLInputElement> {
  label?: string;
  dataTest?: string;
  toggleTip?: ReactElement<ToggleTipProps>;
  checked?: boolean;
}

const StyledCheckbox = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;

  text-transform: none;

  .label {
    flex: 1;
    padding-left: 12px;
  }

  /* hide the browser's default checkbox*/
  input {
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  /* Create a custom checkbox */
  .checkmark {
    flex: 0 0 20px;
    height: 20px;
    width: 20px;
    box-sizing: content-box;
    border-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
    border: ${({ theme }) =>
      `${theme.deprecatedBorder.weight.heavy} solid ${theme.deprecatedColors.gray.eight}`};
    position: relative;
    cursor: pointer;
  }

  /* When the checkbox is checked, add a blue background */
  input:checked ~ .checkmark {
    background-color: ${({ theme }) => theme.deprecatedColors.primary.normal};
    border: ${({ theme }) =>
      `${theme.deprecatedBorder.weight.heavy} solid ${theme.deprecatedColors.primary.normal}`};
  }

  /* add a highlight ring to the checkbox when the hidden input is focused */
  input:focus ~ .checkmark {
    outline: 2px solid Highlight;
    outline: 2px auto -webkit-focus-ring-color;
  }

  /* Create the checkmark/indicator (hidden when not checked) */
  .checkmark:after {
    content: "";
    position: absolute;
    display: none;
  }

  /* Show the checkmark when checked */
  input:checked ~ .checkmark:after {
    display: block;
  }

  /* Style the checkmark/indicator */
  .checkmark:after {
    left: 6px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.deprecatedColors.white};
    border-width: 0 3px 3px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }

  /* Style when disabled */
  input:disabled ~ .checkmark {
    opacity: 0.3;
  }
`;

export enum CheckBoxDisplay {
  normal,
}

export function CheckboxGroup({
  display,
  children,
}: {
  display: CheckBoxDisplay;
  children: TypedReactNode<Props>;
}) {
  return <>{display === CheckBoxDisplay.normal && <>{children}</>}</>;
}

export function Checkbox({ label, dataTest, className, toggleTip, checked, ...inputProps }: Props) {
  const [focused, setFocused] = useState<boolean>(false);
  const defaultClass = checked ? "cb-label selected" : "cb-label";
  const focusedClass = focused ? "focus" : "";
  return (
    <StyledCheckbox
      className={`${defaultClass} ${className || ""} ${focusedClass}`}
      as={typeof label === "undefined" ? "span" : "label"}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <input type="checkbox" className="checkbox" checked={checked} {...inputProps} />
      <span data-test={dataTest} className="checkmark" />
      {label && <span className="label">{label}</span>}
      {toggleTip}
    </StyledCheckbox>
  );
}

export default Checkbox;
